import { useEffect, useMemo, useState } from "react";
import {
  DataGridPremium,
  GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD,
  GridAggregationModel,
  GridColDef,
  GridColumnVisibilityModel,
  GridInitialState,
  GridRenderCellParams,
  GridRowGroupingModel,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import moment from "moment";
import { IconButton, Menu, MenuItem, Tooltip } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEllipsisVertical,
  faFileContract,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import SearchEmptyImg from "components/SearchEmptyImg";
import contractsService from "api/contractsService";
import { IContract } from "dto/Contracts/IContract";
import {
  CONTRACT_SOURCE_LABELS,
  CRU_STATUS_LABELS,
  IContractCruMetric,
  emptyCruMetric,
} from "dto/Contracts/IContractCru";
import InfoIcon from "assets/icons/info_icon.svg?react";
import ContractMetricSlide from "components/Budget/ContractMetricSlide";
import ContractCruMetricModal from "components/ContractCruMetricModal";
import {
  ConfirmCruDialog,
  CruHistoryDialog,
  WithdrawFromCruDialog,
} from "components/ContractCruMetricModal/CruDialogs";
import { ENotificationType, notification } from "utils/notification";
import { getErrorMessage } from "utils/getErrorMessage";
import { IError } from "dto/IError";
import highlightText from "utils/highlightText";
import { ListType } from "dto/ListType";
import { StatusType } from "dto/IStatus";
import { TagType } from "dto/ITag";
import { IGridFilter } from "dto/IGridFilter";
import CustomToolbar from "components/CaseDossier/Toolbar";
import { plGridLocales } from "utils/muiGridLocales";
import { FC, Props } from "./typings";
import styles from "./styles.module.scss";
import useGridRowSelection from "hooks/useGridRowSelection";

const GRID_KEY = "grid_contracts_v1";

// Defined outside the component so DataGridPremium receives a stable slot reference
// (avoids toolbar remounting on every parent re-render).
interface ContractToolbarSlotProps {
  editFilter: IGridFilter;
  setEditFilter: (f: IGridFilter) => void;
  statusId: number;
  setStatusId: (id: number) => void;
  tagId: string;
  setTagId: (id: string) => void;
  userId: string;
  setUserId: (id: string) => void;
  dateFilterLabel: string;
}

const ContractToolbar = (props: ContractToolbarSlotProps) => (
  <CustomToolbar
    listName="contracts"
    editFilter={props.editFilter}
    setEditFilter={props.setEditFilter}
    listType={ListType.Contracts}
    statusId={props.statusId}
    setStatusId={props.setStatusId}
    tagId={props.tagId}
    setTagId={props.setTagId}
    statusType={StatusType.CONTRACTS}
    tagType={TagType.Contracts}
    userId={props.userId}
    setUserId={props.setUserId}
    dateFilterLabel={props.dateFilterLabel}
  />
);

const noticePeriodLabel = (contract: IContract): string => {
  switch (contract.NoticePeriod) {
    case "Week":        return "Tydzień";
    case "TwoWeeks":    return "Dwa tygodnie";
    case "Month":       return "Miesiąc";
    case "ThreeMonths": return "3 miesiące";
    case "Other":       return contract.NoticePeriodDescription ?? "Inne";
    default:            return "";
  }
};

const durationLabel = (contract: IContract): string => {
  if (!contract.Duration) return "";
  if (contract.Duration.Type === "Indefinite") return "Bezterminowa";
  return `Do ${moment(contract.Duration.Until).format("DD.MM.YYYY")}`;
};

const formatPLN = (value: number): string =>
  new Intl.NumberFormat("pl-PL", { maximumFractionDigits: 0 }).format(Math.round(value));

const CustomNoRowsOverlay = () => (
  <div className={styles.empty}>
    <SearchEmptyImg />
    <div>Brak umów w rejestrze</div>
  </div>
);

// Komunikat błędu z odpowiedzi BE (errorCodes) albo tekst zastępczy.
const errText = (e: IError, fallback: string): string =>
  getErrorMessage(e) || fallback;

const contractLabel = (c: IContract) =>
  c.ContractNumber || (c.HasNoAgreementNumber ? "bez numeru" : c.Subject) || c.Id;

// Kolumna „CRU" — etykieta stanu publikacji (port CruPublicationLabel z ContractDescriptor).
const cruLabel = (c: IContract): string => {
  if (!c.IsCruContract) return "";
  if (c.CruPublicationLabel) return c.CruPublicationLabel;
  return c.CanPublishToCru ? "do publikacji" : "metryka CRU";
};

// Akcje CRU na wierszu (port ContractViewModel: ShowMetric / Publish / Update / Withdraw / History).
type CruAction =
  | { kind: "metric"; contract: IContract }
  | { kind: "publish"; contract: IContract }
  | { kind: "update"; contract: IContract }
  | { kind: "withdraw"; contract: IContract }
  | { kind: "history"; contract: IContract }
  | { kind: "remove"; contract: IContract }
  | { kind: "new" };

const ContractRegister: FC<Props> = ({ searchText, setSubtitle }) => {
  const { t } = useTranslation();
  const apiRef = useGridApiRef();
  const { selectedRowIds, rowsRef, handleRowClick } = useGridRowSelection();

  const storedState = (): GridInitialState => {
    try {
      const raw = localStorage.getItem(GRID_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  };

  const [initialState] = useState<GridInitialState>(storedState);

  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(
      () => storedState()?.columns?.columnVisibilityModel ?? {}
    );
  const [groupingModel, setGroupingModel] = useState<GridRowGroupingModel>(
    () => storedState()?.rowGrouping?.model ?? []
  );
  const [aggregationModel, setAggregationModel] =
    useState<GridAggregationModel>({});

  const [editFilter, setEditFilter] = useState<IGridFilter>(null);
  const [metricOf, setMetricOf] = useState<IContract | null>(null);

  // ── CRU ──────────────────────────────────────────────────────────────────
  const [menuAnchor, setMenuAnchor] = useState<{
    el: HTMLElement;
    contract: IContract;
  } | null>(null);
  const [action, setAction] = useState<CruAction | null>(null);
  const [cruMetric, setCruMetric] = useState<IContractCruMetric | null>(null);

  const { mutate: saveCruMetric, isLoading: savingMetric } =
    contractsService.useSaveCruMetric();
  const { mutate: createCruContract, isLoading: creatingCru } =
    contractsService.useCreateCruContract();
  const { mutate: publishToCru, isLoading: publishing } =
    contractsService.usePublishToCru();
  const { mutate: updateInCru, isLoading: updating } =
    contractsService.useUpdateInCru();
  const { mutate: withdrawFromCru, isLoading: withdrawing } =
    contractsService.useWithdrawFromCru();
  const { mutate: removeContract, isLoading: removing } =
    contractsService.useRemoveContract();

  const openMenu = (el: HTMLElement, contract: IContract) =>
    setMenuAnchor({ el, contract });
  const closeMenu = () => setMenuAnchor(null);

  const startAction = (a: CruAction) => {
    closeMenu();
    setAction(a);
    if (a.kind === "new") {
      setCruMetric(emptyCruMetric());
    } else if (a.kind === "metric") {
      setCruMetric(null);
      contractsService
        .getCruMetric(a.contract.Id)
        .then(setCruMetric)
        .catch(() => {
          setAction(null);
          notification({
            type: ENotificationType.ERROR,
            title: "Metryka CRU",
            text: "Nie udało się pobrać metryki umowy.",
          });
        });
    }
  };

  const onCruResult = (title: string, r: { Success: boolean; Message?: string | null; Errors?: Record<string, string> | null }) => {
    const fields = r.Errors
      ? Object.entries(r.Errors)
          .map(([k, v]) => `${k}: ${v}`)
          .join("\n")
      : "";
    notification({
      type: r.Success ? ENotificationType.SUCCESS : ENotificationType.ERROR,
      title,
      text: [r.Message, fields].filter(Boolean).join("\n") || (r.Success ? "OK" : "Błąd"),
    });
  };

  const handleSaveMetric = (metric: IContractCruMetric) => {
    if (!action) return;
    if (action.kind === "new") {
      createCruContract(metric, {
        onSuccess: () => {
          setAction(null);
          notification({
            type: ENotificationType.SUCCESS,
            title: "Rejestr umów",
            text: "Umowa została dodana do rejestru.",
          });
        },
        onError: (e) =>
          notification({
            type: ENotificationType.ERROR,
            title: "Rejestr umów",
            text: errText(e, "Nie udało się zapisać umowy."),
          }),
      });
    } else if (action.kind === "metric") {
      saveCruMetric(
        { id: action.contract.Id, metric },
        {
          onSuccess: () => {
            setAction(null);
            notification({
              type: ENotificationType.SUCCESS,
              title: "Metryka CRU",
              text: "Metryka umowy została zapisana.",
            });
          },
          onError: (e) =>
            notification({
              type: ENotificationType.ERROR,
              title: "Metryka CRU",
              text: errText(e, "Nie udało się zapisać metryki."),
            }),
        },
      );
    }
  };
  const [filterStatusId, setFilterStatusId] = useState<number>(null);
  const [filterTagId, setFilterTagId] = useState<string>(null);
  const [filterUserId, setFilterUserId] = useState<string>(null);

  const saveSnapshotInternal = () => {
    try {
      localStorage.setItem(
        GRID_KEY,
        JSON.stringify(apiRef.current.exportState())
      );
    } catch {}
  };

  const { data, isLoading } = contractsService.useGetContracts(searchText);

  const rows = useMemo(
    () => (data ?? []).map((c) => ({ id: c.Id, ...c })),
    [data]
  );
  rowsRef.current = rows;

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "SourceKind",
        headerName: "Źródło",
        width: 90,
        renderHeader: () => <div className={styles.header}>Źródło</div>,
        valueGetter: (v: IContract["SourceKind"]) =>
          CONTRACT_SOURCE_LABELS[v] ?? v ?? "",
      },
      {
        field: "ContractNumber",
        headerName: "Numer",
        minWidth: 140,
        renderHeader: () => <div className={styles.header}>Numer</div>,
        renderCell: (params: GridRenderCellParams) => {
          const row = params.row as IContract;
          const text =
            params.value || (row?.HasNoAgreementNumber ? "(brak numeru)" : "");
          return (
            <div
              className={styles.cell}
              dangerouslySetInnerHTML={{
                __html: highlightText(text, searchText ?? ""),
              }}
            />
          );
        },
      },
      {
        field: "SignedDate",
        headerName: "Zawarta",
        width: 110,
        renderHeader: () => <div className={styles.header}>Zawarta</div>,
        valueFormatter: (v) => (v ? moment(v).format("DD.MM.YYYY") : ""),
      },
      {
        field: "Parties",
        headerName: "Strony",
        flex: 1,
        minWidth: 160,
        renderHeader: () => <div className={styles.header}>Strony</div>,
        valueGetter: (_: any, row: IContract) =>
          row.SideSummary ?? row.Parties?.map((p) => p.Name).join(", ") ?? "",
        renderCell: (params: GridRenderCellParams) => (
          <div
            className={styles.cell}
            title={params.value}
            dangerouslySetInnerHTML={{
              __html: highlightText(params.value, searchText ?? ""),
            }}
          />
        ),
      },
      {
        field: "Subject",
        headerName: "Przedmiot",
        flex: 1,
        minWidth: 160,
        renderHeader: () => <div className={styles.header}>Przedmiot</div>,
        renderCell: (params: GridRenderCellParams) => (
          <div
            className={styles.cell}
            title={params.value}
            dangerouslySetInnerHTML={{
              __html: highlightText(params.value, searchText ?? ""),
            }}
          />
        ),
      },
      {
        field: "NetAmount",
        headerName: "Netto",
        width: 110,
        type: "number",
        aggregable: true,
        renderHeader: () => <div className={styles.header}>Netto</div>,
        valueFormatter: (v) => (v != null ? formatPLN(v) : ""),
      },
      {
        field: "GrossAmount",
        headerName: "Brutto",
        width: 110,
        type: "number",
        aggregable: true,
        renderHeader: () => <div className={styles.header}>Brutto</div>,
        valueFormatter: (v) => (v != null ? formatPLN(v) : ""),
      },
      {
        field: "Duration",
        headerName: "Wiąże",
        width: 160,
        renderHeader: () => <div className={styles.header}>Wiąże</div>,
        valueGetter: (_: any, row: IContract) => durationLabel(row),
      },
      {
        field: "NoticePeriod",
        headerName: "Wypowiedzenie",
        width: 150,
        renderHeader: () => <div className={styles.header}>Wypowiedzenie</div>,
        valueGetter: (_: any, row: IContract) => noticePeriodLabel(row),
      },
      {
        field: "AgreementStatus",
        headerName: "Status",
        width: 100,
        renderHeader: () => <div className={styles.header}>Status</div>,
        valueGetter: (v: IContract["AgreementStatus"]) =>
          v ? CRU_STATUS_LABELS[v] ?? v : "",
      },
      {
        field: "cru",
        headerName: "CRU",
        width: 190,
        renderHeader: () => <div className={styles.header}>CRU</div>,
        valueGetter: (_: any, row: IContract) => cruLabel(row),
        renderCell: (params: GridRenderCellParams) => {
          if (params.rowNode.type === "group") return <></>;
          const row = params.row as IContract;
          if (!row?.Id) return null;
          return (
            <span
              className={styles.cell}
              style={{
                color: row.IsPublishedInCru
                  ? "#2e7d32"
                  : row.CruWithdrawn
                  ? "#e65100"
                  : undefined,
              }}
              title={row.CruProcessStatus ?? undefined}
            >
              {params.value}
            </span>
          );
        },
      },
      {
        field: "actions",
        headerName: "",
        width: 50,
        sortable: false,
        filterable: false,
        groupable: false,
        hideable: false,
        disableColumnMenu: true,
        disableReorder: true,
        align: "center",
        renderHeader: () => <div className={styles.header}></div>,
        renderCell: (params: GridRenderCellParams) => {
          if (params.rowNode.type === "group") return <></>;
          const row = params.row as IContract;
          if (!row?.Id) return null;
          return (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                openMenu(e.currentTarget, row);
              }}
            >
              <FontAwesomeIcon icon={faEllipsisVertical} style={{ fontSize: 14 }} />
            </IconButton>
          );
        },
      },
      {
        field: "metric",
        headerName: "Met.",
        width: 70,
        sortable: false,
        filterable: false,
        groupable: false,
        hideable: false,
        disableColumnMenu: true,
        disableReorder: true,
        align: "center",
        headerAlign: "center",
        renderHeader: () => <div className={styles.header}>Met.</div>,
        renderCell: (params: GridRenderCellParams) => {
          if (params.rowNode.type === "group") return <></>;
          const row = params.row as IContract;
          if (!row?.Id) return null;
          return (
            <Tooltip title={t("cases.metric")} placement="left">
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 28,
                  height: 28,
                  borderRadius: 4,
                  cursor: "pointer",
                }}
                onClick={() => setMetricOf(row)}
              >
                <InfoIcon style={{ width: 18, height: 18 }} />
              </span>
            </Tooltip>
          );
        },
      },
    ],
    [searchText, t]
  );

  const hiddenFields = [GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD];

  const getTogglableColumns = (columns: GridColDef[]) =>
    columns
      .filter((column) => !hiddenFields.includes(column.field))
      .map((column) => column.field);

  useEffect(() => {
    if (groupingModel.length > 0) {
      setAggregationModel({ NetAmount: "sum", GrossAmount: "sum" });
    } else {
      setAggregationModel({});
    }
  }, [groupingModel]);

  useEffect(() => {
    setSubtitle("Wszystkie");
  }, []);

  const busy = publishing || updating || withdrawing || removing;
  const menuContract = menuAnchor?.contract;

  return (
    <div className={styles.wrapper}>
      <div className={styles.actionsBar}>
        <button
          type="button"
          className={styles.addBtn}
          onClick={() => startAction({ kind: "new" })}
        >
          <FontAwesomeIcon icon={faPlus} /> Nowa umowa
        </button>
      </div>
      <div className={styles.list}>
        <DataGridPremium
          apiRef={apiRef}
          initialState={initialState}
          localeText={plGridLocales}
          slots={{ toolbar: ContractToolbar, noRowsOverlay: CustomNoRowsOverlay }}
          showToolbar
          slotProps={{
            toolbar: {
              editFilter,
              setEditFilter,
              statusId: filterStatusId,
              setStatusId: setFilterStatusId,
              tagId: filterTagId,
              setTagId: setFilterTagId,
              userId: filterUserId,
              setUserId: setFilterUserId,
              dateFilterLabel: t("registry.dateFilterLabel"),
            } as any,
            loadingOverlay: { variant: "skeleton", noRowsVariant: "skeleton" },
            columnsManagement: { getTogglableColumns },
          }}
          loading={isLoading}
          columnHeaderHeight={30}
          columns={columns}
          rows={rows}
          hideFooter
          disableColumnFilter
          disablePivoting
          disableAggregation={false}
          disableRowSelectionOnClick
          rowSelectionModel={selectedRowIds}
          onRowClick={handleRowClick}
          columnVisibilityModel={columnVisibilityModel}
          aggregationModel={aggregationModel}
          sx={{
            border: 0,
            "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
              outline: "none !important",
            },
            "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus":
              { outline: "none !important" },
          }}
          onColumnVisibilityModelChange={(m) => {
            setColumnVisibilityModel(m);
            setTimeout(() => saveSnapshotInternal(), 1000);
          }}
          onColumnOrderChange={() => {
            setTimeout(() => saveSnapshotInternal(), 1000);
          }}
          onRowGroupingModelChange={(m) => {
            const newVisibility = { ...columnVisibilityModel };

            for (let i = 0; i < groupingModel.length; i++) {
              newVisibility[groupingModel[i]] = true;
            }

            for (let i = 0; i < m.length; i++) {
              newVisibility[m[i]] = false;
            }

            setColumnVisibilityModel(newVisibility);
            setGroupingModel(m);
            setTimeout(() => saveSnapshotInternal(), 1000);
          }}
          onAggregationModelChange={() => {
            setTimeout(() => saveSnapshotInternal(), 1000);
          }}
        />
      </div>

      {metricOf && (
        <ContractMetricSlide
          contract={metricOf}
          onClose={() => setMetricOf(null)}
        />
      )}

      {/* ── Menu akcji CRU na wierszu ── */}
      <Menu
        anchorEl={menuAnchor?.el ?? null}
        open={!!menuAnchor}
        onClose={closeMenu}
      >
        {menuContract && (
          <MenuItem onClick={() => startAction({ kind: "metric", contract: menuContract })}>
            <FontAwesomeIcon icon={faFileContract} style={{ marginRight: 8 }} />
            Metryka CRU
          </MenuItem>
        )}
        {menuContract?.CanPublishToCru && (
          <MenuItem onClick={() => startAction({ kind: "publish", contract: menuContract })}>
            Opublikuj w CRU
          </MenuItem>
        )}
        {menuContract?.CanUpdateInCru && (
          <MenuItem onClick={() => startAction({ kind: "update", contract: menuContract })}>
            Aktualizuj w CRU
          </MenuItem>
        )}
        {menuContract?.CanWithdrawFromCru && (
          <MenuItem onClick={() => startAction({ kind: "withdraw", contract: menuContract })}>
            Wycofaj z publikacji w CRU
          </MenuItem>
        )}
        {menuContract?.IsCruContract && (
          <MenuItem onClick={() => startAction({ kind: "history", contract: menuContract })}>
            Historia CRU
          </MenuItem>
        )}
        {menuContract && !menuContract.IsPublishedInCru && (
          <MenuItem
            onClick={() => startAction({ kind: "remove", contract: menuContract })}
            sx={{ color: "#c62828" }}
          >
            Usuń z rejestru
          </MenuItem>
        )}
      </Menu>

      {/* ── Metryka CRU (edycja / nowa umowa) ── */}
      {action && (action.kind === "metric" || action.kind === "new") && cruMetric && (
        <ContractCruMetricModal
          title={action.kind === "new" ? "Nowa umowa (metryka CRU)" : "Metryka umowy (CRU)"}
          subtitle={
            action.kind === "metric"
              ? `${CONTRACT_SOURCE_LABELS[action.contract.SourceKind]} · ${contractLabel(action.contract)}`
              : undefined
          }
          initial={cruMetric}
          saving={savingMetric || creatingCru}
          onSave={handleSaveMetric}
          onClose={() => setAction(null)}
        />
      )}

      {action?.kind === "publish" && (
        <ConfirmCruDialog
          title="Publikacja w CRU"
          text={
            <span>
              Opublikować umowę <b>{contractLabel(action.contract)}</b> w Centralnym
              Rejestrze Umów?
            </span>
          }
          confirmLabel="Opublikuj"
          loading={busy}
          onClose={() => setAction(null)}
          onConfirm={() =>
            publishToCru(action.contract.Id, {
              onSuccess: (r) => {
                setAction(null);
                onCruResult("Publikacja w CRU", r);
              },
              onError: (e) =>
                notification({
                  type: ENotificationType.ERROR,
                  title: "Publikacja w CRU",
                  text: errText(e, "Błąd publikacji."),
                }),
            })
          }
        />
      )}

      {action?.kind === "update" && (
        <ConfirmCruDialog
          title="Aktualizacja w CRU"
          text={
            <span>
              Wysłać aktualne dane umowy <b>{contractLabel(action.contract)}</b> do CRU?
              Bez wpisanej zmiany umowy wysyłana jest „Korekta danych".
            </span>
          }
          confirmLabel="Aktualizuj"
          loading={busy}
          onClose={() => setAction(null)}
          onConfirm={() =>
            updateInCru(action.contract.Id, {
              onSuccess: (r) => {
                setAction(null);
                onCruResult("Aktualizacja w CRU", r);
              },
              onError: (e) =>
                notification({
                  type: ENotificationType.ERROR,
                  title: "Aktualizacja w CRU",
                  text: errText(e, "Błąd aktualizacji."),
                }),
            })
          }
        />
      )}

      {action?.kind === "withdraw" && (
        <WithdrawFromCruDialog
          contractLabel={contractLabel(action.contract)}
          loading={busy}
          onClose={() => setAction(null)}
          onConfirm={(comment) =>
            withdrawFromCru(
              { id: action.contract.Id, comment },
              {
                onSuccess: (r) => {
                  setAction(null);
                  onCruResult("Wycofanie z CRU", r);
                },
                onError: (e) =>
                  notification({
                    type: ENotificationType.ERROR,
                    title: "Wycofanie z CRU",
                    text: errText(e, "Błąd wycofania."),
                  }),
              },
            )
          }
        />
      )}

      {action?.kind === "history" && (
        <CruHistoryDialog
          contractId={action.contract.Id}
          contractLabel={contractLabel(action.contract)}
          onClose={() => setAction(null)}
        />
      )}

      {action?.kind === "remove" && (
        <ConfirmCruDialog
          title="Usunięcie umowy z rejestru"
          text={
            <span>
              Usunąć umowę <b>{contractLabel(action.contract)}</b> z rejestru? Operacja
              nie usuwa pisma ani przesyłki, z której umowa pochodzi.
            </span>
          }
          confirmLabel="Usuń"
          loading={busy}
          onClose={() => setAction(null)}
          onConfirm={() =>
            removeContract(action.contract.Id, {
              onSuccess: () => setAction(null),
              onError: (e) =>
                notification({
                  type: ENotificationType.ERROR,
                  title: "Rejestr umów",
                  text: errText(e, "Nie udało się usunąć umowy."),
                }),
            })
          }
        />
      )}
    </div>
  );
};

export default ContractRegister;
